import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import {
  ContentService,
  CustomerDto,
  CustomersResponse,
  CustomerType,
  ErrorDto,
  KundenField,
  KundenService,
  RetailerDto,
  RetailersResponse
} from 'lion-api';
import { effect, inject, untracked } from '@angular/core';
import { SimpleMessageService, UserStore } from '@shared/data';
import { TranslateService } from '@ngx-translate/core';
import { catchError, of, switchMap, tap } from 'rxjs';
import { Base64 } from 'js-base64';

interface CustomerState {
  customerLoaded: boolean;
  availableCorporatesLoaded: boolean;
  customer: CustomerDto | undefined;
  retailer: RetailerDto | undefined;
  isCustomerDzBank: boolean;
  suggestedCustomers: CustomerDto[];
  suggestedRetailers: RetailerDto[];
  isRefiContext: boolean;
  isCorporate: boolean;
  disabled: boolean;
  adminContext: boolean;
  interestPeriods: string | undefined;
}

const customerState: CustomerState = {
  customerLoaded: false,
  availableCorporatesLoaded: false,
  customer: undefined,
  retailer: undefined,
  isCustomerDzBank: false,
  suggestedCustomers: [],
  suggestedRetailers: [],
  isRefiContext: false,
  isCorporate: false,
  disabled: false,
  adminContext: false,
  interestPeriods: undefined
};

export const CustomerStore = signalStore(
  { providedIn: 'root' },
  withState<CustomerState>(customerState),
  withProps(() => ({
    _kundenService: inject(KundenService),
    _contentService: inject(ContentService),
    _simpleMessageService: inject(SimpleMessageService),
    _translateService: inject(TranslateService),
    _userStore: inject(UserStore),
    _MESSAGE_KEY: 'customer-include'
  })),
  withMethods((store) => {
    let _firmenKundenTestDataMapping = {} as { [key: string]: string };

    return {
      search(query: string) {
        if (query.length < 3) {
          return;
        }

        store._kundenService.customersSearchGET(query, store.adminContext()).subscribe({
          next: (response: CustomersResponse) => {
            const customers: CustomerDto[] = response.customers ?? [];
            patchState(store, { suggestedCustomers: customers });
          },
          error: (error: ErrorDto) => {
            console.log('error', error);
            store._simpleMessageService.sendInlineWarnMessage(
              store._MESSAGE_KEY,
              store._translateService.instant('administration.customer.autoComplete.error.detail')
            );
          }
        });
      },

      setAdminContext(isAdminContext: boolean) {
        patchState(store, { adminContext: isAdminContext });
        if (!isAdminContext) {
          const currentCustomer = store.customer();
          if (currentCustomer && currentCustomer.type === CustomerType.Firmenkunde) {
            const salesUnit = currentCustomer.salesUnit;
            const userGruppe = store._userStore.userGruppe();
            const isFkDzTrader = store._userStore.isFkDzTrader();
            if (isFkDzTrader && salesUnit !== userGruppe) {
              this._patchCustomer(undefined);
            }
          }
        }
      },

      /**
       * Load event for corporate user.
       * Only used through corporate users or dzbank corporate supporters where gp numbers are defined in jwt token.
       */
      _loadAvailableCorporates() {
        store._contentService
          .contentManagementDownloadFileGet('/firmenkunden-testdata-gp-mapping.json')
          .pipe(
            switchMap(async (response: Blob) => JSON.parse(Base64.decode(await response.text()))),
            catchError((err) => {
              console.log('Error while loading firmenkunden-testdata-gp-mapping.json: ', err.message);
              return of(null); // Continue with null
            }),
            tap((mappingJson: { [key: string]: string } | null) => {
              if (mappingJson) {
                console.log('Testdata mapping loaded: ', mappingJson);
                _firmenKundenTestDataMapping = mappingJson;
              }
            }),
            switchMap(() => {
              return store._kundenService.customersByTokenGET().pipe(
                catchError((error: ErrorDto) => {
                  console.log('error getting corporate users', error);
                  store._simpleMessageService.sendInlineWarnMessage(
                    store._MESSAGE_KEY,
                    store._translateService.instant('administration.customer.autoComplete.error.detail')
                  );
                  return of([]);
                })
              );
            })
          )
          .subscribe({
            next: (response: CustomersResponse | CustomerDto[]) => {
              if (Array.isArray(response)) {
                const sorted = [...response].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                patchState(store, { suggestedCustomers: sorted, availableCorporatesLoaded: true });
              } else {
                const customers = response.customers ?? [];
                const sorted = [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                patchState(store, { suggestedCustomers: sorted, availableCorporatesLoaded: true });
              }
            },
            error: (err: ErrorDto) => {
              console.log(`Error while loading customers from jwt token: ${err.message}`);
            }
          });
      },

      reset() {
        patchState(store, {
          customerLoaded: false,
          availableCorporatesLoaded: false,
          customer: undefined,
          retailer: undefined,
          isCustomerDzBank: false,
          suggestedRetailers: [],
          isCorporate: undefined
        });
      },

      disable() {
        patchState(store, {
          disabled: true
        });
      },

      enable() {
        patchState(store, {
          disabled: false
        });
      },

      setCustomer(value: CustomerDto | string, kundeField: KundenField) {
        patchState(store, { suggestedRetailers: [] });
        // Handle empty or invalid input
        if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
          this._patchCustomer(undefined);
          return;
        }

        let customerType: CustomerType = CustomerType.Bankkunde;
        if (typeof value !== 'string' && value.type) {
          customerType = value.type;
        }
        patchState(store, { isCorporate: customerType === CustomerType.Firmenkunde });

        let valueToSearch: string;
        let fieldToSearch: KundenField;

        if (typeof value === 'string') {
          valueToSearch = value;
          fieldToSearch = kundeField;
        } else {
          const customer: CustomerDto = value;
          if (customerType === CustomerType.Firmenkunde && customer.sapGpNumber && _firmenKundenTestDataMapping[customer.sapGpNumber]) {
            valueToSearch = _firmenKundenTestDataMapping[customer.sapGpNumber];
            fieldToSearch = KundenField.Sapgpnummer;
          } else if (!customer.id) {
            // Fallback for objects without an ID, patch what we have.
            this._patchCustomer(customer, customerType);
            return;
          } else {
            valueToSearch = String(customer.id);
            fieldToSearch = KundenField.Id;
          }
        }

        //Replace gps from jwt by internal test data gps if provided
        valueToSearch = this._replaceTestDataGps(customerType, valueToSearch);

        store._kundenService.customerGET(valueToSearch, fieldToSearch, customerType, store.adminContext()).subscribe({
          next: (response) => {
            if (response) {
              const customer = response.customer;
              const interestPeriods = response.interestPeriods;
              if (customer) {
                const actualType = customer.type ?? customerType;
                this._patchCustomer(customer, actualType, interestPeriods);
                patchState(store, { isCorporate: actualType === CustomerType.Firmenkunde });
              } else {
                this._patchCustomer(undefined, customerType);
                store._simpleMessageService.sendInlineWarnMessage(
                  store._MESSAGE_KEY,
                  store._translateService.instant('administration.customer.autoComplete.error.detail')
                );
              }
            }
          },
          error: (error) => {
            console.log('error', error);
            this._patchCustomer(undefined, customerType); // Reset customer on error
            store._simpleMessageService.sendInlineWarnMessage(
              store._MESSAGE_KEY,
              store._translateService.instant('administration.customer.autoComplete.error.detail')
            );
          }
        });
      },

      setRetailer(retailer: RetailerDto | undefined) {
        patchState(store, { retailer: retailer });
      },

      setRefiContext(isRefi: boolean) {
        patchState(store, { isRefiContext: isRefi });
      },

      getRetailerByKundeId(kundeId: number) {
        store._kundenService.searchRetailersGET(kundeId).subscribe({
          next: (response: RetailersResponse) => {
            const retailers = response.retailers ?? [];
            patchState(store, { suggestedRetailers: retailers });
          }
        });
      },

      _patchCustomer(customer: CustomerDto | undefined, customerType: CustomerType = CustomerType.Bankkunde, interestPeriods?: string) {
        const retailer: RetailerDto = {
          type: customerType,
          name: store._userStore.name()
        };
        patchState(store, {
          customer: customer,
          retailer: store._userStore.isDzBankUser() ? undefined : retailer,
          isCustomerDzBank: customer?.murexLabel?.toUpperCase() === 'DZBANK',
          interestPeriods: interestPeriods
        });

        const kundeId = store.customer()?.id;
        if (kundeId) {
          this.getRetailerByKundeId(kundeId);
        } else {
          patchState(store, { suggestedRetailers: [] });
        }
      },

      _replaceTestDataGps(customerType: CustomerType, valueToSearch: string): string {
        if (customerType === CustomerType.Firmenkunde) {
          const mappedValue = _firmenKundenTestDataMapping[valueToSearch];
          if (mappedValue) {
            valueToSearch = mappedValue;
          }
        }
        return valueToSearch;
      }
    };
  }),
  withHooks((store) => {
    return {
      onInit() {
        const userStore = inject(UserStore);
        effect(() => {
          const loaded = userStore.loaded();
          untracked(() => {
            if (!loaded) {
              return;
            }
            if (userStore.isCorporateMode() && !userStore.isDzBankUser()) {
              console.log('Loading available corporates ...');
              store._loadAvailableCorporates();
            }
            console.log('Customer store initialized');
          });
        });
      },
      onDestroy() {
        sessionStorage.clear();
      }
    };
  })
);
